import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import DateSelect from '../components/ui/DateSelect';
import SearchableSelect from '../components/ui/SearchableSelect';
import { useStudentStore } from '../store/StudentStore';
import { useUser } from '../store/UserContext';
import StaffService from '../services/StaffService';

export default function SafeguardingReportPage() {
  const { students, controller } = useStudentStore();
  const { user } = useUser();
  const navigate = useNavigate();
  const staffService = useMemo(() => new StaffService(), []);
  const staffList = useMemo(() => staffService.list(), [staffService]);
  const studentOptions = useMemo(
    () => [...students].sort((a, b) => a.name.localeCompare(b.name)),
    [students]
  );
  const studentSelectOptions = useMemo(
    () =>
      studentOptions.map((student) => ({
        value: student.id,
        label: student.name,
        meta: student.reference || student.id
      })),
    [studentOptions]
  );
  const staffOptions = useMemo(
    () =>
      staffList.map((worker) => ({
        value: worker.name,
        label: worker.name,
        meta: worker.role || worker.title || ''
      })),
    [staffList]
  );
  const [form, setForm] = useState({
    studentId: '',
    type: '',
    severity: 'medium',
    status: 'open',
    description: '',
    date: new Date().toISOString().slice(0, 10),
    assignedOwner: user?.name || '',
    reviewDate: '',
    escalationLevel: 'L1',
    attachments: ''
  });
  const [files, setFiles] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!form.studentId && studentOptions.length > 0) {
      setForm((prev) => ({ ...prev, studentId: studentOptions[0].id }));
    }
  }, [form.studentId, studentOptions]);

  const submitReport = () => {
    if (!form.studentId) {
      setError('Select a student for this report.');
      return;
    }
    if (!form.type.trim() || !form.description.trim()) {
      setError('Type and description are required.');
      return;
    }
    const fileAttachments = files.map((file) => file.name);
    const textAttachments = form.attachments
      ? form.attachments.split(',').map((item) => item.trim()).filter(Boolean)
      : [];

    controller.addSafeguardingEvent(
      form.studentId,
      {
        category: 'Social Care',
        type: form.type.trim(),
        severity: form.severity,
        status: form.status,
        description: form.description.trim(),
        date: form.date,
        assignedOwner: form.assignedOwner,
        reviewDate: form.reviewDate || null,
        escalationLevel: form.escalationLevel,
        attachments: [...textAttachments, ...fileAttachments],
        createdBy: user?.name || 'System'
      },
      user
    );
    navigate('/students?view=safeguarding');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="sp-value" style={{ fontSize: '20px', fontWeight: 600 }}>Log Safeguarding Report</h2>
          <p className="text-sm sp-muted">Record a safeguarding concern against a student.</p>
        </div>
        <Button variant="secondary" size="sm" onClick={() => navigate('/students?view=safeguarding')}>
          Back to safeguarding
        </Button>
      </div>

      <Card className="p-5">
        {error && (
          <div className="sp-alert sp-alert--critical text-sm mb-4">
            {error}
          </div>
        )}
        <div className="grid md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="sp-label">Student</label>
            <SearchableSelect
              value={form.studentId}
              options={studentSelectOptions}
              placeholder="Search students"
              emptyLabel="No students found."
              ariaLabel="Search students"
              className="mt-2"
              onChange={(value) => setForm({ ...form, studentId: value })}
            />
          </div>
          <div>
            <label className="sp-label">Type</label>
            <input
              value={form.type}
              onChange={(event) => setForm({ ...form, type: event.target.value })}
              className="sp-input mt-2 w-full"
              placeholder="e.g. Disclosure"
            />
          </div>
          <div>
            <label className="sp-label">Date</label>
            <DateSelect
              value={form.date}
              onChange={(value) => setForm({ ...form, date: value })}
              className="mt-2 w-full"
            />
          </div>
          <div>
            <label className="sp-label">Severity</label>
            <select
              value={form.severity}
              onChange={(event) => setForm({ ...form, severity: event.target.value })}
              className="sp-select mt-2 w-full"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>
          <div>
            <label className="sp-label">Status</label>
            <select
              value={form.status}
              onChange={(event) => setForm({ ...form, status: event.target.value })}
              className="sp-select mt-2 w-full"
            >
              <option value="open">Open</option>
              <option value="monitoring">Monitoring</option>
              <option value="closed">Closed</option>
            </select>
          </div>
          <div>
            <label className="sp-label">Assigned owner</label>
            <SearchableSelect
              value={form.assignedOwner}
              options={[{ value: '', label: 'Unassigned' }, ...staffOptions]}
              placeholder="Search staff"
              emptyLabel="No staff found."
              ariaLabel="Search assigned owner"
              allowCustomValue
              className="mt-2"
              onChange={(value) => setForm({ ...form, assignedOwner: value })}
            />
          </div>
          <div>
            <label className="sp-label">Assigned owner (manual)</label>
            <input
              value={form.assignedOwner}
              onChange={(event) => setForm({ ...form, assignedOwner: event.target.value })}
              className="sp-input mt-2 w-full"
              placeholder="Enter name"
            />
          </div>
          <div>
            <label className="sp-label">Review / follow-up date</label>
            <DateSelect
              value={form.reviewDate}
              onChange={(value) => setForm({ ...form, reviewDate: value })}
              className="mt-2 w-full"
            />
          </div>
          <div>
            <label className="sp-label">Escalation level</label>
            <select
              value={form.escalationLevel}
              onChange={(event) => setForm({ ...form, escalationLevel: event.target.value })}
              className="sp-select mt-2 w-full"
            >
              <option value="L1">L1</option>
              <option value="L2">L2</option>
              <option value="L3">L3</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="sp-label">Description</label>
            <textarea
              value={form.description}
              onChange={(event) => setForm({ ...form, description: event.target.value })}
              rows={4}
              className="sp-textarea mt-2 w-full"
            />
          </div>
          <div className="md:col-span-2">
            <label className="sp-label">Attachments (comma-separated)</label>
            <input
              value={form.attachments}
              onChange={(event) => setForm({ ...form, attachments: event.target.value })}
              className="sp-input mt-2 w-full"
              placeholder="e.g. case-note.pdf, referral.docx"
            />
          </div>
          <div className="md:col-span-2">
            <label className="sp-label">Attach files</label>
            <input
              type="file"
              multiple
              className="sp-input mt-2 w-full"
              onChange={(event) => setFiles(Array.from(event.target.files || []))}
            />
            {files.length > 0 && (
              <div className="text-xs sp-muted mt-2">
                {files.map((file) => file.name).join(', ')}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 mt-6">
          <Button variant="secondary" size="sm" onClick={() => navigate('/students?view=safeguarding')}>
            Cancel
          </Button>
          <Button size="sm" onClick={submitReport}>
            Save Report
          </Button>
        </div>
      </Card>
    </div>
  );
}
